import {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { MaskedTextBox } from "@progress/kendo-react-inputs";

import "./RCInput.scss";
import { useValidation } from "../utils/ValidationProvider";

export const extractFromRC = (rc, part = "YY") => {
  if (!rc) return rc;
  rc = rc.replace("/", "");

  switch(part) {
    case "YY":
      return rc.substr(0, 2);

    case "XX":
    case "MM":
      return rc.substr(2, 2);

    case "DD":
      return rc.substr(4, 2);

    case "SSS":
      return rc.substr(6, 3);

    case "SSSC":
      return rc.substr(6, 4);
    
    case "C":
      return rc[9];

    default:
      return "";
  }
}

export const checkRCValidity = val => {
  if (!val) return false;
  val = val.replace("/", "");
  if (isNaN(Number(val))) return false;

  const validNumbers = /^[0-9]{2}/;
  const allNumbers = /[0-9]/g;

  const yy = extractFromRC(val, "YY");
  const mm = extractFromRC(val, "MM");
  const dd = extractFromRC(val, "DD");
  const sssc = extractFromRC(val, "SSSC");

  const checkValidSSSC = new RegExp(`^[0-9]{${sssc.length}}`);

  const validYY = validNumbers.test(yy);
  const validMMbf2054 = (1 <= Number(mm) && Number(mm) <= 12) || (51 <= Number(mm) && Number(mm) <= 62);
  const validMMaf2054 = Number(yy) >= 54 && ((21 <= Number(mm) && Number(mm) <= 32) || (71 <= Number(mm) && Number(mm) <= 82));
  const validMM = validNumbers.test(mm) && (validMMbf2054 || validMMaf2054);
  const validDD = validNumbers.test(dd) && (1 <= Number(dd) && Number(dd) <= 31);
  const validSSSC = checkValidSSSC.test(sssc);

  const valid = validYY && validMM && validDD && validSSSC && ((sssc.length === 3 && val.length === 9) || (sssc.length === 4 && val.length === 10 && Number(val) % 11 === 0)) && allNumbers.test(val);

  return valid;
}

/**
 * Input na rodne cislo
 * @module
 * @param {string} name - Name
 * @param {string} type - Format rodneho cisla, zatial iba "sk"
 * @param {string} [value] - hodnota Rodneho cisla
 * @param {func} onChange - Function invoked on value change
 * @param {func} [onBlur] - Function invoked on blur
 * @param {func} [onFocus] - Function invoked on focus
 * @param {string} [className] - className
 * @param {bool} [disabled] - disabled
 * @param {string} [placeholder] - placeholder
 * @param {bool} [required] - required
 * @param {number} [tabIndex] - tabIndex
 * @param {bool} [validationHidden] - validationHidden
 * @param {string} [validationMessage] - validationMessage
 *
 **/


export const RCInput = forwardRef((props, ref) => {
  const [value, setValue] = useState("");
  const [valueInvalid, setValueInvalid] = useState(false);
  const isC = useRef(false);
  const inputRef = useRef(null);

  //test
  useEffect(() => {
    if (inputRef.current && inputRef.current.element && props.name) {
      inputRef.current.element.setAttribute('data-testid', props.name);
    }
  }, [props.name])

  const field = useMemo(() => ({
    field: props.name,
    validationMessage: (value.replace(/[_/]/g, "") === "" || value === "") && props.required ? props.validationMessage : "Nesprávna hodnota rodného čísla.",
    validationAllowed: props.validationAllowed,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    isInvalid: (field, value, data) => !checkRCValidity(value) && !(!props.required && (value?.replace(/[_/]/g, "") === "" || value === "" || value === undefined))
  }), [props.name, props.validationMessage, props.validationAllowed, props.required, props.recommended, props.disabled, value]);

  const { validate, setValidationValue } = useValidation(field, props.value);

  useEffect(() => {
    if(props.value){
      let val = props.value.includes("/") ? props.value : props.value.substr(0, 6) + "/" + extractFromRC(props.value, "SSSC");
      
      isC.current = !isNaN(Number(extractFromRC(val, "C")));
      setValue(val);
    }
  }, [props.value]);

  useEffect(() => {
    if (inputRef.current && value && value === getMask().replace(/[0LA]/g, "_")) {
      inputRef.current._input.selectionStart = 0;
      inputRef.current._input.selectionEnd = 0;
      inputRef.current._selection = [0, 0];
    }
    isC.current = !isNaN(Number(extractFromRC(value, "C")));
    // eslint-disable-next-line
  }, [value]);

  useImperativeHandle(ref, () => {
    return {
      value,
      setValue,
      valueInvalid,
      inputRef
    };
  });

  const getType = () => props.type ?? "sk";

  const masks = {
    "sk-bf54": "000000/000",
    "sk-af54": "000000/0000"
  };

  const getMask = (fromValue = null) => {
    // before or after 1954
    const bf54 = "-bf54";
    const af54 = "-af54";
    let key = getType();

    fromValue = fromValue ?? value;
    if (fromValue) {
      const yy = Number(extractFromRC(fromValue, "YY"));
      const m = Number(extractFromRC(fromValue, "MM")[0]);
      //                                            checking for rc that is before 1954
      if (!isNaN(yy) && yy < 54 && !isC.current && (isNaN(m) || [0, 1, 5, 6].includes(m))) {
        key = key + bf54;
      }
      else key = key + af54;
    }

    if (key === getType()) key = key + af54;
    return masks[key];
  }
  
  const onChange = e => {
    let insertedValue = e.nativeEvent?.data;
    let newValue = e?.value;

    // check if pasted correct RC
    if (insertedValue && e.nativeEvent.inputType === "insertFromPaste" && checkRCValidity(insertedValue)) {
      newValue = insertedValue.includes("/") ? insertedValue : insertedValue.substr(0, 6) + "/" + extractFromRC(insertedValue, "SSSC");
    }

    isC.current = !isNaN(Number(extractFromRC(newValue, "C")));
    if (newValue) {
      const yy = Number(extractFromRC(newValue, "YY"));
      // add extra number at the end of RC, applies for 2000-2053
      const correctInsert = insertedValue && (/[0-9]/g).test(insertedValue) && insertedValue.length === 1;
      if (correctInsert && e.selectionStart === e.selectionEnd && e.selectionStart === newValue.length && !isNaN(yy) && yy < 54 && newValue.length === 10 && !value.includes("_")) {
        newValue = newValue + insertedValue;
        isC.current = true;
        inputRef.current._selection[1]++;
        inputRef.current._selection[0]++;
      }

      const mask = getMask(newValue);
      if (newValue.length > mask.length) newValue = newValue.substr(0, mask.length);
      else if (newValue.length < mask.length) newValue = newValue + "_";
    }

    const valid = checkRCValidity(newValue);
    setValue(newValue);
    if (props.onChange) props.onChange(props.name, valid ? newValue.replace("/", "") : null);
  }

  const onBlur = () => {
    let newValue = value;

    if (newValue && newValue === getMask().replace(/[0LA]/g, "_")) {
      newValue = "";
      setValue("");
    }

    const valid = checkRCValidity(newValue);
    setValueInvalid(!valid);

    setValidationValue(newValue);
    validate();

    if (props.onBlur) props.onBlur();
  }

  const onFocus = () => {
    if (!value) setValue(getMask().replace(/[0LA]/g, "_"));
    if (props.onFocus) props.onFocus();
  }

  return (
    <>
      <span
        className={`form-input rc-input k-input k-input-md k-input-solid k-widget ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")} ${valueInvalid ? "dpop-invalid" : ""}`}
      >
        <MaskedTextBox
          className={"k-input masked-tb " + (props.className ?? "")}
          ref={inputRef}
          includeLiterals={true}
          name={props.name}
          valid={!valueInvalid || props.disabled}
          mask={getMask()}
          placeholder={
            props.disabled ? "" : props.placeholder
          }
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={props.disabled}
          validityStyles={null}
          required={props.required}
          tabIndex={props.tabIndex}
        ></MaskedTextBox>
      </span>
    </>
  );
});

RCInput.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  validationHidden: PropTypes.bool,
  validationMessage: PropTypes.string,
  tabIndex: PropTypes.number,
  className: PropTypes.string
};

export default RCInput;
